import React, { createContext, useState, useContext, useCallback, useEffect, useRef } from 'react'
import { useAuth } from '../hooks/useAuth'
import {
  addGuestItem,
  clearGuestCart,
  getGuestCart,
  hasGuestItems,
  mergeGuestCart,
  removeGuestItem,
  updateGuestQty,
  GuestCustomPayload,
} from '../utils/guestCart'
import { MetaPixel } from '../utils/metaPixel'
import { trackAddToCart } from '../utils/analytics'

const GATEWAY = process.env.REACT_APP_GATEWAY_URL || 'http://localhost:4000'

export interface CartItem {
  id:           string
  product_type: 'catalogue' | 'custom'
  product_id:   string
  product_name: string
  quantity:     number
  /** Unit price as it stands NOW — the server reprices custom lines on read. */
  price_at_add: number
  /**
   * Struck-through "was" price, or null/absent when there isn't an honest one.
   * Display only: it is never part of any total, and the server omits it unless
   * it is genuinely above the price being charged.
   */
  compare_at_price?: number | null
  mood?:        string
  image_url?:   string
}

// All money is computed server-side (ecommerce-service/src/lib/pricing.ts) and
// sent down whole. Prices are GST-INCLUSIVE: `total` is what the customer pays,
// and `subtotal + tax === total` is the breakdown shown for transparency.
// The UI must render these values, never recompute them.
//
// A GUEST has no server cart (the cart API is authenticated by design), so their
// bag is built client-side by utils/guestCart with the same shape and the same
// GST split — see that module for why.
export interface Cart {
  items:      CartItem[]
  total:      number   // GST-inclusive — the payable amount
  subtotal:   number   // base price excluding GST (display only)
  tax:        number   // GST contained within `total` (display only)
  item_count: number
}

const EMPTY_CART: Cart = { items: [], total: 0, subtotal: 0, tax: 0, item_count: 0 }

interface CartContextType {
  cart:         Cart
  isOpen:       boolean
  isLoading:    boolean
  /** True while a guest bag is being replayed into the account on sign-in. */
  isMerging:    boolean
  /**
   * True once the bag has actually been resolved for the current visitor — the
   * guest bag read, or the signed-in cart fetched (after any merge).
   *
   * `isLoading` cannot answer this: it tracks add/remove calls, not the initial
   * read, so it is false both before the first fetch and after it. Anything that
   * must not act on an empty-looking cart — the ?opencart=1 deep link, which
   * would otherwise open an empty drawer every time — waits on this instead.
   */
  isReady:      boolean
  openCart:     () => void
  closeCart:    () => void
  addItem:      (productType: string, productId: string, quantity?: number) => Promise<void>
  /**
   * Add a freshly generated formulation. Signed in, this creates the custom
   * product then adds it; as a guest it goes to the local bag carrying the
   * payload needed to recreate it after login.
   */
  addCustomItem: (
    payload: GuestCustomPayload,
    price: number,
    compareAtPrice?: number | null,
  ) => Promise<void>
  removeItem:   (itemId: string) => Promise<void>
  updateQty:    (itemId: string, quantity: number) => Promise<void>
  clearCart:    () => Promise<void>
  refreshCart:  () => Promise<void>
}

const CartContext = createContext<CartContextType | null>(null)

export function CartProvider({ children }: { children: React.ReactNode }) {
  const { getToken, user } = useAuth()
  const [cart, setCart]       = useState<Cart>(EMPTY_CART)
  const [isOpen, setIsOpen]   = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isMerging, setIsMerging] = useState(false)
  // WHICH identity the resolved bag belongs to — not merely "has something
  // resolved". `undefined` = nothing yet, `null` = the guest bag, a string =
  // that signed-in user's cart.
  //
  // This has to be an identity rather than a boolean, and `isReady` has to be
  // DERIVED during render rather than cleared in an effect. Child effects run
  // before parent effects, so a consumer's effect sees the new `user` in the
  // same commit that this provider's effect has not run in yet — a boolean
  // reset here would still be one commit late, and the consumer would read
  // "ready" off the previous identity's bag. Comparing during render closes
  // that window: the instant `user` changes, isReady is already false.
  const [readyFor, setReadyFor] = useState<string | null | undefined>(undefined)
  // One merge per session. Without this, React StrictMode's double-invoked
  // effects (and any re-render of the user effect) would replay the bag twice.
  const mergedFor = useRef<string | null>(null)

  async function authFetch(url: string, options: RequestInit = {}) {
    const token = await getToken()
    return fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        ...options.headers
      },
      credentials: 'include'
    })
  }

  const refreshCart = useCallback(async () => {
    // Guests read their bag out of localStorage — there is nothing to fetch.
    if (!user) { setCart(getGuestCart()); return }
    try {
      const res = await authFetch(`${GATEWAY}/api/cart`)
      if (res.ok) setCart(await res.json())
    } catch { /* silent */ }
  }, [user])

  // On sign-in, fold any guest bag into the real cart BEFORE reading it, so the
  // customer never sees their items briefly vanish. Covers every entry point
  // (the checkout redirect, a plain login, a restored session), not just the
  // returnUrl path.
  useEffect(() => {
    let cancelled = false

    async function syncCart() {
      if (!user) {
        mergedFor.current = null
        setCart(getGuestCart())
        if (!cancelled) setReadyFor(null)
        return
      }

      if (mergedFor.current !== user.id && hasGuestItems()) {
        mergedFor.current = user.id
        setIsMerging(true)
        try {
          const token = await getToken()
          if (token) await mergeGuestCart(token, user.id)
          else clearGuestCart()
        } catch (err) {
          console.error('[cart] guest cart merge failed:', err)
        } finally {
          if (!cancelled) setIsMerging(false)
        }
      } else {
        mergedFor.current = user.id
      }

      if (!cancelled) await refreshCart()
      // Last, and only after the merge and the fetch have both settled — this
      // is the flag a deep link waits on before deciding the bag is empty.
      if (!cancelled) setReadyFor(user.id)
    }

    syncCart()
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user])

  const addItem = useCallback(async (productType: string, productId: string, quantity = 1) => {
    setIsLoading(true)
    try {
      if (!user) {
        // The guest bag stores the display fields the drawer needs, so read them
        // off the public product endpoint (GET /api/products is unauthenticated).
        const res = await fetch(`${GATEWAY}/api/products/${productId}`)
        if (!res.ok) throw new Error('Product not found')
        const product = await res.json()

        setCart(addGuestItem({
          product_type: productType as CartItem['product_type'],
          product_id:   productId,
          product_name: product.name,
          price_at_add: product.price,
          mood:         product.mood || undefined,
          image_url:    product.image_url || undefined,
          quantity,
        }))
        setIsOpen(true)
        // Only after the bag actually took it. Every catalogue add-to-cart in
        // the app routes through this one function (Shop, ProductDetail, the
        // collection strip), so the event lives here and nowhere else —
        // duplicating it at the call sites would double-fire.
        //
        // `value` is the money this add is worth, so it must be multiplied by
        // the quantity: ProductDetail lets the customer pick one, and sending
        // the unit price for a qty-3 add understates it threefold.
        MetaPixel.addToCart(product.price * quantity)
        trackAddToCart(product.name, product.price * quantity, 'product')
        return
      }

      const res = await authFetch(`${GATEWAY}/api/cart/items`, {
        method: 'POST',
        body: JSON.stringify({ product_type: productType, product_id: productId, quantity })
      })
      if (res.ok) {
        const updated: Cart = await res.json()
        setCart(updated)
        setIsOpen(true)
        // The server is the only authority on a signed-in line's price, so read
        // the figure back off the line it just wrote rather than trusting
        // anything the client had. Missing line → no event, rather than a
        // guessed value.
        //
        // Unit price × the quantity ADDED — deliberately not the line's own
        // quantity, which already includes anything that was in the bag before
        // this click and would over-report a re-add.
        const line = updated.items.find(
          i => i.product_id === productId && i.product_type === productType
        )
        if (line) {
          MetaPixel.addToCart(line.price_at_add * quantity)
          trackAddToCart(line.product_name, line.price_at_add * quantity, 'product')
        }
      }
    } catch (err) {
      console.error('[cart] add item failed:', err)
    } finally { setIsLoading(false) }
  }, [user])

  // `price` / `compareAtPrice` are used ONLY for a guest line — a signed-in
  // customer's line is priced by the cart API, which is the only price charged.
  const addCustomItem = useCallback(async (
    payload: GuestCustomPayload,
    price: number,
    compareAtPrice: number | null = null,
  ) => {
    setIsLoading(true)
    try {
      if (!user) {
        // No account yet → no custom_products row can exist. Keep the creation
        // payload with the line so the merge can build it after sign-in.
        setCart(addGuestItem({
          product_type:   'custom',
          // The formulation id is the stable key for this formula — the same one
          // the server dedupes custom products on.
          product_id:     payload.formulation_id,
          product_name:   payload.name,
          price_at_add:   price,
          compare_at_price: compareAtPrice,
          mood:           payload.mood || undefined,
          custom_payload: payload,
        }))
        setIsOpen(true)
        return
      }

      const productRes = await authFetch(`${GATEWAY}/api/products/custom`, {
        method: 'POST',
        body: JSON.stringify({ user_id: user.id, ...payload }),
      })
      if (!productRes.ok) throw new Error('Failed to save formulation')
      const product = await productRes.json()

      const cartRes = await authFetch(`${GATEWAY}/api/cart/items`, {
        method: 'POST',
        body: JSON.stringify({ product_type: 'custom', product_id: product.id, quantity: 1 }),
      })
      if (!cartRes.ok) throw new Error('Failed to add to cart')

      setCart(await cartRes.json())
      setIsOpen(true)
    } finally { setIsLoading(false) }
  }, [user])

  const removeItem = useCallback(async (itemId: string) => {
    if (!user) { setCart(removeGuestItem(itemId)); return }
    const res = await authFetch(`${GATEWAY}/api/cart/items/${itemId}`, { method: 'DELETE' })
    if (res.ok) setCart(await res.json())
  }, [user])

  const updateQty = useCallback(async (itemId: string, quantity: number) => {
    if (!user) { setCart(updateGuestQty(itemId, quantity)); return }
    const res = await authFetch(`${GATEWAY}/api/cart/items/${itemId}`, {
      method: 'PATCH',
      body: JSON.stringify({ quantity })
    })
    if (res.ok) setCart(await res.json())
  }, [user])

  const clearCart = useCallback(async () => {
    if (!user) { setCart(clearGuestCart()); return }
    const res = await authFetch(`${GATEWAY}/api/cart`, { method: 'DELETE' })
    if (res.ok) setCart(EMPTY_CART)
  }, [user])

  // Computed during render, so it can never describe a stale identity.
  const isReady = readyFor === (user?.id ?? null)

  return (
    <CartContext.Provider value={{
      cart, isOpen, isLoading, isMerging, isReady,
      openCart:  () => setIsOpen(true),
      closeCart: () => setIsOpen(false),
      addItem, addCustomItem, removeItem, updateQty, clearCart, refreshCart
    }}>
      {children}
    </CartContext.Provider>
  )
}

export function useCart() {
  const ctx = useContext(CartContext)
  if (!ctx) throw new Error('useCart must be used within CartProvider')
  return ctx
}
